/**
 * =============================================================================
 * FALCON - Module Deneigement des Parkings Avion
 * =============================================================================
 *
 * @file        TwoFactorSetup.tsx
 * @description Composant de configuration de la double authentification TOTP
 *              Guide l'utilisateur a travers les etapes de configuration :
 *              1. Affichage du QR code a scanner
 *              2. Verification du code OTP
 *              3. Affichage des codes de secours
 *
 * @version     2.5
 * @author      Equipe FALCON
 * =============================================================================
 */

import { useState, useEffect } from 'react';
import { twoFactorService, type TwoFactorSetupResponse } from '@/services/twoFactorService';
import {
  QrCodeIcon,
  ShieldCheckIcon,
  DocumentDuplicateIcon,
  CheckCircleIcon,
  XMarkIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

interface TwoFactorSetupProps {
  /** Callback appele lorsque la configuration est terminee */
  onComplete: () => void;
  /** Callback appele lorsque l'utilisateur annule */
  onCancel: () => void;
}

type SetupStep = 'qrcode' | 'verify' | 'backup';

/**
 * Composant de configuration de la double authentification
 *
 * Processus en 3 etapes :
 * 1. Scan du QR code avec Google Authenticator ou autre app TOTP
 * 2. Saisie du code OTP pour verifier la configuration
 * 3. Sauvegarde des codes de secours
 */
export function TwoFactorSetup({ onComplete, onCancel }: TwoFactorSetupProps) {
  const [step, setStep] = useState<SetupStep>('qrcode');
  const [setupData, setSetupData] = useState<TwoFactorSetupResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [otpCode, setOtpCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [backupCodesCopied, setBackupCodesCopied] = useState(false);

  // Charger les donnees de configuration au montage
  useEffect(() => {
    initSetup();
  }, []);

  const initSetup = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await twoFactorService.setup();
      setSetupData(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur lors de l\'initialisation';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!otpCode || otpCode.length !== 6) {
      toast.error('Veuillez saisir un code a 6 chiffres');
      return;
    }

    setIsVerifying(true);
    try {
      await twoFactorService.verify(otpCode);
      setStep('backup');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Code invalide';
      toast.error(message);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleCopyBackupCodes = () => {
    if (setupData?.backupCodes) {
      const codesText = setupData.backupCodes.join('\n');
      navigator.clipboard.writeText(codesText);
      setBackupCodesCopied(true);
      toast.success('Codes copies dans le presse-papiers');
    }
  };

  const handleComplete = () => {
    if (!backupCodesCopied) {
      toast.error('Veuillez copier ou noter vos codes de secours avant de continuer');
      return;
    }
    onComplete();
  };

  // Affichage du chargement
  if (isLoading) {
    return (
      <div className="p-6 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
        <p className="text-gray-600">Preparation de la configuration...</p>
      </div>
    );
  }

  // Affichage de l'erreur
  if (error) {
    return (
      <div className="p-6">
        <div className="text-center text-red-600 mb-4">
          <ExclamationTriangleIcon className="w-12 h-12 mx-auto mb-2" />
          <p>{error}</p>
        </div>
        <div className="flex justify-center space-x-3">
          <button onClick={onCancel} className="btn-secondary">
            Annuler
          </button>
          <button onClick={initSetup} className="btn-primary">
            Reessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-900">
          Configuration de la double authentification
        </h2>
        <button onClick={onCancel} className="text-gray-400 hover:text-gray-600">
          <XMarkIcon className="w-6 h-6" />
        </button>
      </div>

      {/* Indicateur d'etapes */}
      <div className="flex items-center justify-center mb-8">
        {(['qrcode', 'verify', 'backup'] as SetupStep[]).map((s, index) => (
          <div key={s} className="flex items-center">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
                ${
                  step === s
                    ? 'bg-primary-600 text-white'
                    : index < ['qrcode', 'verify', 'backup'].indexOf(step)
                    ? 'bg-green-500 text-white'
                    : 'bg-gray-200 text-gray-600'
                }`}
            >
              {index < ['qrcode', 'verify', 'backup'].indexOf(step) ? (
                <CheckCircleIcon className="w-5 h-5" />
              ) : (
                index + 1
              )}
            </div>
            {index < 2 && (
              <div
                className={`w-12 h-1 mx-1 ${
                  index < ['qrcode', 'verify', 'backup'].indexOf(step)
                    ? 'bg-green-500'
                    : 'bg-gray-200'
                }`}
              />
            )}
          </div>
        ))}
      </div>

      {/* Etape 1: QR Code */}
      {step === 'qrcode' && setupData && (
        <div>
          <div className="text-center mb-6">
            <QrCodeIcon className="w-12 h-12 text-primary-600 mx-auto mb-2" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Scannez le QR code</h3>
            <p className="text-sm text-gray-600">
              Utilisez une application d'authentification comme Google Authenticator, Authy ou
              Microsoft Authenticator pour scanner ce code.
            </p>
          </div>

          {/* QR Code - utilisation d'une image generee par l'API */}
          <div className="flex justify-center mb-6">
            <div className="p-4 bg-white border rounded-lg">
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(
                  setupData.qrCodeUrl
                )}`}
                alt="QR Code pour 2FA"
                className="w-48 h-48"
              />
            </div>
          </div>

          {/* Code manuel */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <p className="text-xs text-gray-500 mb-2">
              Vous ne pouvez pas scanner le code ? Entrez cette cle manuellement :
            </p>
            <code className="text-sm font-mono bg-white px-3 py-2 rounded border block text-center select-all">
              {setupData.secret}
            </code>
          </div>

          <div className="flex justify-end">
            <button
              onClick={() => setStep('verify')}
              className="btn-primary flex items-center"
            >
              Suivant
              <ArrowRightIcon className="w-4 h-4 ml-2" />
            </button>
          </div>
        </div>
      )}

      {/* Etape 2: Verification */}
      {step === 'verify' && (
        <div>
          <div className="text-center mb-6">
            <ShieldCheckIcon className="w-12 h-12 text-primary-600 mx-auto mb-2" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Verifiez la configuration</h3>
            <p className="text-sm text-gray-600">
              Entrez le code a 6 chiffres affiche dans votre application d'authentification.
            </p>
          </div>

          <div className="max-w-xs mx-auto mb-6">
            <label htmlFor="otpCode" className="label text-center block">
              Code de verification
            </label>
            <input
              id="otpCode"
              type="text"
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              className="input text-center text-2xl tracking-widest font-mono"
              placeholder="000000"
              maxLength={6}
              autoFocus
            />
          </div>

          <div className="flex justify-between">
            <button
              onClick={() => setStep('qrcode')}
              className="btn-secondary flex items-center"
            >
              <ArrowLeftIcon className="w-4 h-4 mr-2" />
              Retour
            </button>
            <button
              onClick={handleVerifyCode}
              disabled={isVerifying || otpCode.length !== 6}
              className="btn-primary flex items-center"
            >
              {isVerifying ? 'Verification...' : 'Verifier'}
              <ArrowRightIcon className="w-4 h-4 ml-2" />
            </button>
          </div>
        </div>
      )}

      {/* Etape 3: Codes de secours */}
      {step === 'backup' && setupData && (
        <div>
          <div className="text-center mb-6">
            <CheckCircleIcon className="w-12 h-12 text-green-500 mx-auto mb-2" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Double authentification activee !
            </h3>
            <p className="text-sm text-gray-600">
              Sauvegardez ces codes de secours. Ils vous permettront de vous connecter si vous
              perdez l'acces a votre application d'authentification.
            </p>
          </div>

          {/* Avertissement */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <div className="flex items-start">
              <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600 mr-2 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-yellow-800 font-medium">Important</p>
                <p className="text-sm text-yellow-700">
                  Chaque code ne peut etre utilise qu'une seule fois. Conservez-les dans un
                  endroit sur.
                </p>
              </div>
            </div>
          </div>

          {/* Codes de secours */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <div className="grid grid-cols-2 gap-2">
              {setupData.backupCodes.map((code, index) => (
                <code
                  key={index}
                  className="text-sm font-mono bg-white px-3 py-2 rounded border text-center"
                >
                  {code}
                </code>
              ))}
            </div>
          </div>

          {/* Bouton copier */}
          <button
            onClick={handleCopyBackupCodes}
            className={`w-full flex items-center justify-center py-2 rounded-lg border mb-6 transition-colors
              ${
                backupCodesCopied
                  ? 'bg-green-50 border-green-300 text-green-700'
                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
          >
            {backupCodesCopied ? (
              <>
                <CheckCircleIcon className="w-5 h-5 mr-2" />
                Codes copies
              </>
            ) : (
              <>
                <DocumentDuplicateIcon className="w-5 h-5 mr-2" />
                Copier les codes
              </>
            )}
          </button>

          <div className="flex justify-end">
            <button
              onClick={handleComplete}
              disabled={!backupCodesCopied}
              className="btn-primary"
            >
              Terminer
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default TwoFactorSetup;
